// ── Assignments / projects section (Wave 9, REAL own-scoped read) ───────────
// Own assignment/project context for the participant's OWN cohort. Reuses the
// existing Z6 assignments read (cohorts.assignments, GET /cohorts/:id/
// assignments — F10), Jotform-sourced; the lane is dark in mock + provider-gated,
// so an empty result reads as an honest empty (never fabricated assignment rows).
//
// ISOLATION (FR-PDASH-001): read for the participant's OWN cohort only. The admin
// assignments view shows EVERY participant's submission rows; the participant
// dashboard shows ONLY the per-week SUMMARY for their own cohort — it never lists
// peer participant emails / per-person submission rows (those are admin-only). So
// this section surfaces the own-cohort weekly assignment STATUS (whether work is
// due / submitted) without any cross-participant row. REPORT-EXCLUSION: no report.

import { CollectionPagination, CollectionToolbar, DataTableV2, IconDoc } from "@vjal/ui";
import { useMemo } from "react";

import { useHonestQuery } from "../../components/honest-state";
import type { CoachingCohortDto, ListResponse } from "../../services/coaching-dtos";
import type { CoachingAssignmentLogDto } from "../../services/coaching-z6-dtos";
import { ParticipantSectionShell } from "./participant-section-shell";
import {
  participantQueryKeys,
  readOwnCohortAssignments,
  readOwnCohorts,
} from "./participant-dashboard-helpers";
import {
  useParticipantAssignmentsCollection,
  type ParticipantAssignmentRow,
} from "./participant-assignments-collection";

export function ParticipantAssignmentsSection() {
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    participantQueryKeys.ownCohorts(),
    readOwnCohorts
  );
  const cohorts = cohortsQuery.data?.items ?? [];
  const primaryCohortId = cohorts[0]?.id ?? "";

  const assignmentsQuery = useHonestQuery<CoachingAssignmentLogDto>(
    participantQueryKeys.assignments(primaryCohortId || "missing"),
    () => readOwnCohortAssignments(primaryCohortId),
    {
      enabled: Boolean(primaryCohortId),
      disabledPosture: "empty",
      isEmpty: (data) => (data.assignments?.weeks ?? []).length === 0,
    }
  );

  const allRows = useMemo<ParticipantAssignmentRow[]>(() => {
    const weeks = assignmentsQuery.data?.assignments.weeks ?? [];
    return weeks.map((week) => ({
      id: String(week.weekNumber),
      week: `Week ${week.weekNumber}`,
      weekNumber: week.weekNumber,
      submitted: week.submittedCount > 0 ? "submissions recorded" : "nothing submitted yet",
    }));
  }, [assignmentsQuery.data?.assignments.weeks]);

  const collection = useParticipantAssignmentsCollection(allRows);
  const showToolbar = assignmentsQuery.posture === null;

  if (cohortsQuery.posture !== null || !primaryCohortId) {
    return (
      <ParticipantSectionShell
        title="Assignments"
        subtitle="Your weekly assignment context."
        posture={cohortsQuery.posture ?? "empty"}
        gate={cohortsQuery.gate}
        reference={cohortsQuery.reference}
        onRetry={cohortsQuery.retry}
        emptyTitle="No assignments yet"
        emptyMessage="Your assignment context will appear here once you're enrolled in a cohort."
        icon={<IconDoc size={22} />}
        testId="participant-assignments-section"
      >
        <span />
      </ParticipantSectionShell>
    );
  }

  return (
    <ParticipantSectionShell
      title="Assignments"
      subtitle="Your weekly assignment context (your cohort). Submissions are recorded as you complete them."
      posture={assignmentsQuery.posture}
      gate={assignmentsQuery.gate}
      reference={assignmentsQuery.reference}
      onRetry={assignmentsQuery.retry}
      emptyTitle="No assignments yet"
      emptyMessage="Weekly assignments will appear here once they're set for your cohort."
      icon={<IconDoc size={22} />}
      testId="participant-assignments-section"
    >
      {showToolbar ? (
        <CollectionToolbar
          collection={collection}
          searchPlaceholder="Search by week…"
          className="rounded-xl border border-line"
          stableChipNames
        />
      ) : null}
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Your weekly assignments"
          emptyTitle="No assignments match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" ? <CollectionPagination collection={collection} className="px-4" /> : null}
      </div>
    </ParticipantSectionShell>
  );
}